import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createMemoStyles, defaultFonts, defaultBaseline, visuallyHidden, uniqueId, safeRest, KEYS } from '@wds/utils';
import { useTheme } from 'react-jss';
import { Tooltip } from '@wds/tooltip';
import { Label } from '@wds/label';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    radius = theme.radius,
    shadow = theme.shadow;
  var slider = {
    handleBGColor: palette.muted.contrast,
    trackBGColor: palette.muted.main,
    fillBGColor: palette.primary.main,
    borderRadius: radius.medium,
    height: 16
  };
  return {
    wrenchLabel: _assign({}, defaultFonts(theme)),
    wrenchSlider: function wrenchSlider() {
      return _assign(_assign({}, defaultBaseline), {
        display: 'flex',
        alignItems: 'center',
        height: 24
      });
    },
    wrenchSliderFill: function wrenchSliderFill() {
      return {
        position: 'absolute',
        background: slider.fillBGColor,
        height: '100%',
        borderRadius: slider.borderRadius,
        zIndex: 1
      };
    },
    wrenchSliderTrack: function wrenchSliderTrack() {
      return {
        background: slider.trackBGColor,
        borderRadius: slider.borderRadius,
        height: slider.height,
        position: 'relative',
        flex: 1
      };
    },
    wrenchSliderHandle: function wrenchSliderHandle() {
      return {
        position: 'absolute',
        display: 'block',
        textAlign: 'center',
        height: slider.height + spacing[2],
        width: slider.height + spacing[2],
        border: 'none',
        background: slider.handleBGColor,
        borderRadius: slider.borderRadius,
        top: -spacing[2] / 2,
        zIndex: 2,
        transform: 'translateX(-50%)',
        boxShadow: shadow.sky,
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing'
        }
      };
    },
    wrenchSliderHandleFill: function wrenchSliderHandleFill() {
      return {
        height: slider.height + spacing[2],
        width: slider.height + spacing[2]
      };
    },
    wrenchSliderSteps: function wrenchSliderSteps() {
      return {
        padding: 0,
        margin: 0,
        display: 'flex',
        justifyContent: 'space-between',
        height: '100%',
        listStyle: 'none'
      };
    },
    wrenchVisuallyHidden: function wrenchVisuallyHidden() {
      return _assign({}, visuallyHidden);
    }
  };
});
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    radius = theme.radius;
  var slider = {
    fillBGColor: palette.primary.main,
    borderRadius: radius.medium
  };
  return {
    wrenchSliderFill: function wrenchSliderFill() {
      return {
        position: 'absolute',
        background: slider.fillBGColor,
        height: '100%',
        borderRadius: slider.borderRadius,
        zIndex: 1
      };
    }
  };
});
var ProgressBar = function ProgressBar(_a) {
  var style = _a.style;
  var theme = useTheme();
  var classes = useStyles$1({
    theme: theme
  });
  return React.createElement("span", {
    className: classes.wrenchSliderFill,
    style: style
  }, "\xA0");
};
var PERCENT_EMPTY = 0;
var PERCENT_FULL = 100;
var getPosition = function getPosition(value, min, max) {
  return (value - min) / (max - min) * 100;
};
var getValue = function getValue(pos, min, max) {
  var decimal = pos / 100;
  return Math.round((max - min) * decimal + min);
};
var getClosestSnapPoint = function getClosestSnapPoint(value, snapPoints) {
  if (!(snapPoints === null || snapPoints === void 0 ? void 0 : snapPoints.length)) return value;
  return snapPoints.reduce(function (snapA, snapB) {
    var diffA = Math.abs(snapA - value);
    var diffB = Math.abs(snapB - value);
    if (diffA === diffB) {
      // If difference between snap points is the same return the smaller value
      return snapA < snapB ? snapA : snapB;
      // If difference between snap points is the same return the greater value
      // return snapA > snapB ? snapA : snapB;
    }

    return diffB < diffA ? snapB : snapA;
  });
};
var getHandleFor = function getHandleFor(ev) {
  var _a, _b;
  return ((_a = ev.currentTarget) === null || _a === void 0 ? void 0 : _a.getAttribute) ? Number((_b = ev.currentTarget) === null || _b === void 0 ? void 0 : _b.getAttribute('data-handle-key')) : null;
};
var killEvent = function killEvent(ev) {
  ev.stopPropagation();
  ev.preventDefault();
};
var Slider = function Slider(_a) {
  var alwaysShowTooltip = _a.alwaysShowTooltip,
    ariaLabel = _a.ariaLabel,
    children = _a.children,
    controlled = _a.controlled,
    disabled = _a.disabled,
    id = _a.id,
    label = _a.label,
    min = _a.min,
    max = _a.max,
    onChange = _a.onChange,
    range = _a.range,
    showTooltip = _a.showTooltip,
    snap = _a.snap,
    snapPoints = _a.snapPoints,
    tooltipValue = _a.tooltipValue,
    values = _a.values,
    valueText = _a.valueText,
    rest = __rest(_a, ["alwaysShowTooltip", "ariaLabel", "children", "controlled", "disabled", "id", "label", "min", "max", "onChange", "range", "showTooltip", "snap", "snapPoints", "tooltipValue", "values", "valueText"]);
  if (!label && !ariaLabel && !id) {
    // eslint-disable-next-line no-console
    console.warn('Accessibility Error: If `label` or `ariaLabel` is not provided the `id` prop is required');
  }
  var inputId = id !== null && id !== void 0 ? id : uniqueId('input');
  var _b = useState(values),
    localValues = _b[0],
    setValues = _b[1];
  var _c = useState(null),
    slidingIndex = _c[0],
    setSlidingIndex = _c[1];
  var _d = useState(0),
    handleDimensions = _d[0],
    setHandleDimensions = _d[1];
  var _e = useState({
      height: 0,
      left: 0,
      top: 0,
      width: 0
    }),
    sliderBox = _e[0],
    setSliderBox = _e[1];
  // Used to manage position state during events
  var posRef = useRef();
  // HandlePos should only recalculate when localValues updates
  var handlePos = useCallback(function () {
    var localPos = localValues.map(function (value) {
      return getPosition(value, min, max);
    });
    posRef.current = {
      handlePos: localPos
    };
    return localPos;
  }, [localValues, min, max])();
  var theme = useTheme();
  var classes = useStyles$2({
    theme: theme
  });
  // Ref to internal state needed in listeneres
  var stateRef = useRef({
    slidingIndex: slidingIndex,
    handleDimensions: handleDimensions,
    sliderBox: sliderBox
  });
  var setStateRef = function setStateRef(state) {
    stateRef.current = _assign({}, state);
    setSlidingIndex(state.slidingIndex);
    setHandleDimensions(state.handleDimensions);
    setSliderBox(state.sliderBox);
  };
  // Ref to Slider so we can get dimensions etc.
  var sliderRef = useRef(null);
  /// //////////////////////////////////////////// Validations Start ////////////////////////////////////////////
  var validateValues = function validateValues(proposedValues) {
    return proposedValues.map(function (value, idx) {
      // Bound Value between max and min
      var realValue = Math.max(Math.min(value, max), min);
      // If not a number for some reason take min instead
      realValue = Number.isNaN(realValue) ? min : realValue;
      if (idx !== 0 && realValue < proposedValues[idx - 1]) {
        return proposedValues[idx - 1];
      }
      return realValue;
    });
  };
  var validatePosition = function validatePosition(idx, proposedPosition) {
    return Math.max(Math.min(proposedPosition, handlePos[idx + 1] !== undefined ? handlePos[idx + 1] - stateRef.current.handleDimensions : PERCENT_FULL // 100% is the highest value
    ), handlePos[idx - 1] !== undefined ? handlePos[idx - 1] + stateRef.current.handleDimensions : PERCENT_EMPTY // 0% is the lowest value
    );
  };

  var canMove = function canMove(idx, proposedPosition) {
    if (proposedPosition < PERCENT_EMPTY) return false;
    if (proposedPosition > PERCENT_FULL) return false;
    var nextHandlePosition = handlePos[idx + 1] !== undefined ? handlePos[idx + 1] - handleDimensions : Infinity;
    if (proposedPosition > nextHandlePosition) return false;
    var prevHandlePosition = handlePos[idx - 1] !== undefined ? handlePos[idx - 1] + handleDimensions : -Infinity;
    return proposedPosition >= prevHandlePosition;
  };
  var getMinValue = function getMinValue(idx) {
    return localValues[idx - 1] ? Math.max(min, localValues[idx - 1]) : min;
  };
  var getMaxValue = function getMaxValue(idx) {
    return localValues[idx + 1] ? Math.min(max, localValues[idx + 1]) : max;
  };
  /// ///////////////////////////////// Validations End /////////////////////////////////
  /// ///////////////////////////////// Value Calculations Start /////////////////////////////////
  var getNextPositionForKey = function getNextPositionForKey(idx, key) {
    var _a;
    var originalPercentage = handlePos[idx];
    var shouldSnap = snap;
    var proposedValue = localValues[idx];
    var proposedPercentage = originalPercentage;
    var stepValue = 1;
    if (max - min >= 100) {
      proposedPercentage = Math.round(proposedPercentage);
    } else {
      stepValue = 100 / (max - min);
    }
    var currentIndex = null;
    if (shouldSnap && snapPoints) {
      currentIndex = snapPoints.indexOf(getClosestSnapPoint(localValues[idx], snapPoints));
    }
    var stepMultiplier = (_a = {}, _a[KEYS.LEFT] = function (v) {
      return v * -1;
    }, _a[KEYS.RIGHT] = function (v) {
      return v * 1;
    }, _a[KEYS.UP] = function (v) {
      return v * 1;
    }, _a[KEYS.DOWN] = function (v) {
      return v * -1;
    }, _a[KEYS.PAGE_DOWN] = function (v) {
      return v > 1 ? -v : v * -10;
    }, _a[KEYS.PAGE_UP] = function (v) {
      return v > 1 ? v : v * 10;
    }, _a);
    if (Object.prototype.hasOwnProperty.call(stepMultiplier, key)) {
      proposedPercentage += stepMultiplier[key](stepValue);
      if (shouldSnap && snapPoints && currentIndex !== null) {
        if (proposedPercentage > originalPercentage) {
          // move cursor right unless overflow
          if (currentIndex < snapPoints.length - 1) {
            proposedValue = snapPoints[currentIndex + 1];
          }
          // move cursor left unless there is overflow
        } else if (currentIndex > 0) {
          proposedValue = snapPoints[currentIndex - 1];
        }
      }
    } else if (key === KEYS.HOME) {
      proposedPercentage = PERCENT_EMPTY;
      if (shouldSnap && snapPoints) {
        proposedValue = snapPoints[0];
      }
    } else if (key === KEYS.END) {
      proposedPercentage = PERCENT_FULL;
      if (shouldSnap && snapPoints) {
        proposedValue = snapPoints[snapPoints.length - 1];
      }
    } else {
      return null;
    }
    return shouldSnap ? getPosition(proposedValue, min, max) : proposedPercentage;
  };
  var getNextValues = function getNextValues(idx, proposedPosition) {
    var actualPosition = validatePosition(idx, proposedPosition);
    var nextHandlePos = handlePos.map(function (pos, index) {
      return index === idx ? actualPosition : pos;
    });
    return nextHandlePos.map(function (pos) {
      return getValue(pos, min, max);
    });
  };
  var getProgressStyle = function getProgressStyle(idx) {
    var value = handlePos[idx];
    var prevValue = handlePos[idx - 1];
    var diffValue = value - prevValue;
    if (idx === 0) {
      return {
        left: 0,
        width: "".concat(value, "%")
      };
    }
    return {
      left: "".concat(prevValue, "%"),
      width: "".concat(diffValue, "%")
    };
  };
  var getSliderBoundingBox = function getSliderBoundingBox() {
    var _a, _b, _c, _d, _e, _f;
    var slider = sliderRef.current;
    var rect = slider === null || slider === void 0 ? void 0 : slider.getBoundingClientRect();
    return {
      height: (_b = (_a = rect === null || rect === void 0 ? void 0 : rect.height) !== null && _a !== void 0 ? _a : slider === null || slider === void 0 ? void 0 : slider.clientHeight) !== null && _b !== void 0 ? _b : 0,
      left: (_c = rect === null || rect === void 0 ? void 0 : rect.left) !== null && _c !== void 0 ? _c : 0,
      top: (_d = rect === null || rect === void 0 ? void 0 : rect.top) !== null && _d !== void 0 ? _d : 0,
      width: (_f = (_e = rect === null || rect === void 0 ? void 0 : rect.width) !== null && _e !== void 0 ? _e : slider === null || slider === void 0 ? void 0 : slider.clientWidth) !== null && _f !== void 0 ? _f : 0
    };
  };
  var getHandleDimensions = function getHandleDimensions(ev, box) {
    var _a;
    var handleNode = (_a = ev.currentTarget) !== null && _a !== void 0 ? _a : null;
    if (!handleNode) return 0;
    return handleNode.clientWidth / box.width * PERCENT_FULL / 2;
  };
  var getSnapPosition = function getSnapPosition(positionPercent) {
    if (!snap) return positionPercent;
    var value = getValue(positionPercent, min, max);
    var snapValue = getClosestSnapPoint(value, snapPoints);
    return getPosition(snapValue, min, max);
  };
  var getClosestHandle = function getClosestHandle(positionPercent) {
    return handlePos.reduce(function (closestIdx, node, idx) {
      var challenger = Math.abs(handlePos[idx] - positionPercent);
      var current = Math.abs(handlePos[closestIdx] - positionPercent);
      return challenger < current ? idx : closestIdx;
    }, 0);
  };
  // ///////////////////////////////// Value Calculations End /////////////////////////////////
  // ///////////////////////////////// Update Values Start /////////////////////////////////
  // Used to update internal values and send them to the onChange.
  var updateValues = function updateValues(proposedValues) {
    var realValue = validateValues(proposedValues);
    setValues(realValue);
  };
  var updateOnChange = function updateOnChange(proposedValues) {
    var realValue = validateValues(proposedValues);
    setValues(realValue);
    // Fire on Change
    if (onChange) {
      onChange({
        values: realValue,
        min: min,
        max: max
      });
    }
  };
  var slideTo = function slideTo(idx, proposedPosition, triggerChange) {
    if (triggerChange) {
      updateOnChange(getNextValues(idx, proposedPosition));
    } else {
      updateValues(getNextValues(idx, proposedPosition));
    }
  };
  // ///////////////////////////////// Update Values End /////////////////////////////////
  // ///////////////////////////////// Handlers Start /////////////////////////////////
  var handleClick = function handleClick(event) {
    if (event.target.getAttribute('data-handle-key')) {
      return;
    }
    // Calculate the position of the slider on the page so we can determine
    // the position where you click in relativity.
    var box = getSliderBoundingBox();
    var positionDecimal = (event.clientX - box.left) / box.width;
    var positionPercent = positionDecimal * PERCENT_FULL;
    var handleId = getClosestHandle(positionPercent);
    var validPositionPercent = getSnapPosition(positionPercent);
    // Move the handle there
    slideTo(handleId, validPositionPercent, true);
  };
  var handleKeydown = function handleKeydown(event) {
    var _a;
    var idx = getHandleFor(event);
    if (idx === null) {
      return;
    }
    if (event.key === KEYS.ESC) {
      (_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
      return;
    }
    var proposedPercentage = getNextPositionForKey(idx, event.key);
    if (proposedPercentage === null) return;
    if (canMove(idx, proposedPercentage)) {
      slideTo(idx, proposedPercentage, true);
    }
    killEvent(event);
  };
  var setStartSlide = function setStartSlide(event) {
    var box = getSliderBoundingBox();
    var dimensions = getHandleDimensions(event, box);
    var index = getHandleFor(event);
    setStateRef({
      slidingIndex: index,
      handleDimensions: dimensions,
      sliderBox: box
    });
  };
  var handleSlide = function handleSlide(x) {
    var positionPercent = (x - stateRef.current.sliderBox.left) / stateRef.current.sliderBox.width * PERCENT_FULL;
    if (stateRef.current.slidingIndex !== null) {
      slideTo(stateRef.current.slidingIndex, positionPercent, false);
    }
  };
  var handleTouchSlide = function handleTouchSlide(event) {
    if (stateRef.current.slidingIndex === null) return;
    if (event.changedTouches.length > 1) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      endSlide();
      return;
    }
    var touch = event.changedTouches[0];
    handleSlide(touch.clientX);
    killEvent(event);
  };
  var handleMouseSlide = function handleMouseSlide(event) {
    if (stateRef.current.slidingIndex === null) return;
    handleSlide(event.clientX);
    killEvent(event);
  };
  var endSlide = function endSlide() {
    document.removeEventListener('mouseup', endSlide, false);
    document.removeEventListener('touchend', endSlide, false);
    document.removeEventListener('touchmove', handleTouchSlide, false);
    document.removeEventListener('mousemove', handleMouseSlide, false);
    if (snap && posRef.current && stateRef.current.slidingIndex !== null) {
      var pos = posRef.current.handlePos[stateRef.current.slidingIndex];
      var positionPercent = getSnapPosition(pos);
      slideTo(stateRef.current.slidingIndex, positionPercent, true);
    }
    setStateRef(_assign(_assign({}, stateRef.current), {
      slidingIndex: null
    }));
    if (posRef.current) {
      updateOnChange(posRef.current.handlePos.map(function (x) {
        return getValue(x, min, max);
      }));
    }
  };
  var captureClick = function captureClick(event) {
    event.stopPropagation(); // Stop the click from being propagated.
    document.removeEventListener('click', captureClick, true); // cleanup
  };

  var startMouseSlide = function startMouseSlide(event) {
    setStartSlide(event);
    document.addEventListener('mousemove', handleMouseSlide, false);
    document.addEventListener('mouseup', endSlide, false);
    // This registeres a listener for the capture phase instead of the bubbling phase!
    document.addEventListener('click', captureClick, true);
    killEvent(event);
  };
  var startTouchSlide = function startTouchSlide(event) {
    if (event.changedTouches.length > 1) return;
    setStartSlide(event);
    document.addEventListener('touchmove', handleTouchSlide, false);
    document.addEventListener('touchend', endSlide, false);
    killEvent(event);
  };
  // ///////////////////////////////// Handlers End /////////////////////////////////
  // ///////////////////////////////// Effects Start /////////////////////////////////
  // If we are controlled and get some prop updates we should update state
  useEffect(function () {
    // Don't update any state
    if (!controlled) return;
    // Update State
    setValues(validateValues(values));
    // Fire onChange if it exists
    if (onChange) onChange({
      values: values,
      min: min,
      max: max
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), min, max]); // Stringify values to catch object changes
  // Update Values if Min/Max Change
  useEffect(function () {
    if (controlled) {
      updateOnChange(localValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max]);
  // If suddenly Disable Stop Sliding and disable
  useEffect(function () {
    if (disabled && slidingIndex !== null) {
      endSlide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);
  // ///////////////////////////////// Effects End /////////////////////////////////
  return React.createElement(React.Fragment, null, label && React.createElement("div", {
    className: classes.wrenchLabel,
    id: inputId
  }, label), React.createElement("div", _assign({
    className: classes.wrenchSlider,
    ref: sliderRef,
    onClick: disabled ? undefined : handleClick
  }, safeRest(rest)), React.createElement("div", {
    className: classes.wrenchSliderTrack
  }, handlePos.map(function (pos, idx) {
    return React.createElement("div", {
      role: "slider",
      "aria-labelledby": label ? inputId : undefined,
      "aria-label": ariaLabel,
      "aria-valuemax": getMaxValue(idx),
      "aria-valuemin": getMinValue(idx),
      "aria-valuenow": localValues[idx],
      "aria-disabled": disabled,
      "aria-valuetext": valueText,
      "data-handle-key": idx,
      className: classes.wrenchSliderHandle,
      // eslint-disable-next-line react/no-array-index-key
      key: "handle-".concat(idx),
      onClick: killEvent,
      onKeyDown: disabled ? undefined : handleKeydown,
      onMouseDown: disabled ? undefined : startMouseSlide,
      onTouchStart: disabled ? undefined : startTouchSlide,
      style: {
        left: "".concat(pos, "%")
      },
      tabIndex: 0
    }, React.createElement("span", {
      className: classes.wrenchVisuallyHidden
    }, localValues[idx]), (showTooltip || alwaysShowTooltip) && React.createElement(Tooltip, {
      text: tooltipValue ? tooltipValue(localValues[idx]) : localValues[idx],
      alwaysShow: alwaysShowTooltip
    }, React.createElement("div", {
      className: classes.wrenchSliderHandleFill
    })));
  }), handlePos.map(function (node, idx, arr) {
    if (idx === 0 && arr.length > 1 || range === false) {
      return null;
    }
    return React.createElement(ProgressBar
    // eslint-disable-next-line react/no-array-index-key
    , {
      // eslint-disable-next-line react/no-array-index-key
      key: "progress-bar-".concat(idx),
      style: getProgressStyle(idx)
    });
  }), children && React.createElement("ul", {
    className: classes.wrenchSliderSteps
  }, children))));
};
Slider.defaultProps = {
  alwaysShowTooltip: false,
  ariaLabel: undefined,
  children: undefined,
  controlled: false,
  disabled: false,
  id: undefined,
  label: undefined,
  onChange: undefined,
  range: true,
  showTooltip: false,
  snap: false,
  snapPoints: [],
  tooltipValue: undefined,
  valueText: undefined
};
var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    palette = theme.palette;
  return {
    wrenchStep: function wrenchStep() {
      return {
        listStyle: 'none',
        flexGrow: 1,
        height: '100%',
        borderRight: "1px solid ".concat(palette.background.light),
        position: 'relative',
        '&:nth-last-child(2)': {
          borderRight: '1px solid transparent'
        },
        // // All labels
        '& > * ': {
          position: 'absolute',
          top: '100%',
          transform: 'translateX(-50%)',
          marginTop: spacing[1]
        },
        '&:first-child': {
          textAlign: 'left',
          '& > *': {
            transform: 'translateX(0)'
          }
        },
        '&:last-child': {
          borderRight: 'none',
          maxWidth: 0,
          '& > *': {
            right: 0,
            transform: 'translateX(0)'
          }
        }
      };
    }
  };
});
var Step = function Step(_a) {
  var children = _a.children;
  var theme = useTheme();
  var classes = useStyles({
    theme: theme
  });
  return React.createElement("li", {
    className: classes.wrenchStep
  }, children && React.createElement(Label, {
    muted: true
  }, children));
};
export { Slider, Step, getClosestSnapPoint };